import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { apiUrl } from '../config/api';
import ResponsiveTable from '../components/ResponsiveTable';
import StatusBadge from '../components/UI/StatusBadge';
import { useTableSort } from '../components/UI/tableSort';
import Button from '../components/UI/Button';
import FullScreenPanel from '../components/UI/FullScreenPanel';
import KpiCard from '../components/Admin/KpiCard';
import { usersMetrics } from '../utils/adminMetrics';
import { exportToCsv } from '../utils/adminExport';
import { AuthContext } from '../context/AuthContext';
import { auditModalView } from '../utils/auditClient';
import Pager from '../components/UI/Pager';

const PAGE_SIZE = 10;

const ManageUsers = () => {
  const { user } = useContext(AuthContext);
  const hasPerm = (p) => Array.isArray(user?.permissions) && user.permissions.includes(p);
  const canManageUsers = hasPerm('ManageUsers') || hasPerm('ManageConfig');
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({ email: '', roleId: '', isActive: true });
  const [message, setMessage] = useState('');
  const [formErrors, setFormErrors] = useState({ email: '', roleId: '' });
  const [confirmAction, setConfirmAction] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => setMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page on search
  }, [searchTerm]);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(apiUrl('/api/users'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUsers(response.data);
      setError(null);
    } catch (err) {
      setError('Error fetching users: ' + err.message);
    }
  };

  const fetchRoles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(apiUrl('/api/roles'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRoles(response.data.filter(role => role.is_active)); // Only active roles
    } catch (err) {
      console.error('Error fetching roles:', err);
      setRoles([]); // Fallback to empty array
    }
  };

  const validateForm = () => {
    let valid = true;
    const errors = { email: '', roleId: '' };

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      errors.email = 'Email is required';
      valid = false;
    } else if (!emailRegex.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
      valid = false;
    }

    if (!formData.roleId) {
      errors.roleId = 'Role is required';
      valid = false;
    }

    setFormErrors(errors);
    return valid;
  };

  const addUser = async () => {
    if (!validateForm()) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(apiUrl('/api/register'), formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowModal(false);
      setFormData({ email: '', roleId: '', isActive: true });
      setFormErrors({ email: '', roleId: '' });
      setMessage('User added successfully');
      fetchUsers();
    } catch (err) {
      const errorResponse = err.response?.data;
      let errorMsg = 'Registration failed';
      if (errorResponse?.error?.includes('duplicate key value violates unique constraint')) {
        errorMsg = 'Email already exists';
      } else if (errorResponse?.error) {
        errorMsg = errorResponse.error;
      }
      setMessage(errorMsg);
    }
  };

  const updateUser = async () => {
    if (!validateForm()) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(apiUrl(`/api/users/${selectedUser.id}`), formData, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setShowModal(false);
      setFormData({ email: '', roleId: '', isActive: true });
      setFormErrors({ email: '', roleId: '' });
      setMessage('User updated successfully');
      fetchUsers();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Error updating user');
    }
  };

  const handleStatusChange = async (userId, action) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = action === 'activate' ? `api/users/${userId}/activate` : `api/users/${userId}/deactivate`;
      await axios.put(apiUrl(`/${endpoint}`), {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage(`User ${action}d successfully`);
      setConfirmAction(null);
      fetchUsers();
    } catch (err) {
      setMessage(err.response?.data?.error || `Error ${action}ing user`);
    }
  };

  const openConfirmDialog = (user, action) => {
    setConfirmAction({ userId: user.id, action, email: user.email });
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      roleId: user.role_id.toString(),
      isActive: user.is_active
    });
    setMessage('');
    setFormErrors({ email: '', roleId: '' });
    setShowModal(true);
    auditModalView('Edit User');
  };

  const openAddModal = () => {
    setSelectedUser(null);
    setFormData({ email: '', roleId: '', isActive: true });
    setMessage('');
    setFormErrors({ email: '', roleId: '' });
    setShowModal(true);
    auditModalView('Add User');
  };

  const getMessageClass = () => {
    if (message.includes('Error') || message === 'Email already exists' || message === 'Registration failed') {
      return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300';
    } else if (message.includes('successfully')) {
      return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
    }
    return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300';
  };

  const renderActions = (user) => {
    if (!canManageUsers) return null;
    return (
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            openEditModal(user);
          }}
          className="px-2.5 py-1 text-xs font-medium rounded-md transition-colors bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300"
        >
          Edit
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            openConfirmDialog(user, user.is_active ? 'deactivate' : 'activate');
          }}
          className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${user.is_active
            ? 'bg-rose-50 text-rose-600 hover:bg-rose-100 dark:bg-rose-900/30 dark:text-rose-300'
            : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-900/30 dark:text-emerald-300'}`}
        >
          {user.is_active ? 'Deactivate' : 'Activate'}
        </button>
      </div>
    );
  };

  // Search and pagination logic
  const filteredUsers = users.filter(user =>
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.role_name && user.role_name.toLowerCase().includes(searchTerm.toLowerCase()))
  );
  const totalPages = Math.ceil(filteredUsers.length / PAGE_SIZE);
  // The table's rows for every match, so a click on a column sorts them all
  // before the page is taken.
  const userRows = filteredUsers.map(user => ({
    id: user.id,
    email: user.email,
    role_name: user.role_name || 'N/A',
    is_active: user.is_active,
    user: user // Store the whole user object for reference
  }));
  const userSort = useTableSort(userRows);
  const paginatedUsers = userSort.rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  // Build columns dynamically based on permissions
  const columns = [
    {
      field: 'email',
      header: 'Email',
      formatter: (value) => <span className="font-medium">{value}</span>
    },
    {
      field: 'role_name',
      header: 'Role'
    },
    {
      field: 'is_active',
      header: 'Active',
      formatter: (value) => (
        <StatusBadge status={value ? 'Active' : 'Inactive'} label={value ? 'Yes' : 'No'} />
      )
    },
  ];
  if (canManageUsers) {
    columns.push({
      field: 'actions',
      header: 'Actions',
      formatter: (_, row) => renderActions(row.user)
    });
  }

  return (
    <div className="max-w-full min-h-screen">

      {/* KPI Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-3">
        {(() => {
          const m = usersMetrics(users);
          return (
            <>
              <KpiCard label="Total Users" value={m.total} color="blue" />
              <KpiCard label="Active Users" value={m.active} color="green" />
              <KpiCard label="Inactive Users" value={m.inactive} color="red" />
            </>
          );
        })()}
      </div>

      {error && <div className="p-2 rounded-lg mb-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">{error}</div>}
      {message && (
        <div className={`p-2 rounded-lg mb-2 flex items-center justify-between ${getMessageClass()}`}>
          <span>{message}</span>
          <button
            onClick={() => setMessage('')}
            className="ml-4 px-2 py-1 rounded-full text-lg font-bold hover:bg-gray-200 focus:outline-none"
            aria-label="Close"
            title="Close"
          >
            &times;
          </button>
        </div>
      )}

      {confirmAction && (
        <div className="flex items-center justify-between bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 p-2 rounded mb-4 shadow border border-yellow-200 dark:border-yellow-700/50">
          <span>
            Are you sure you want to <strong>{confirmAction.action}</strong> user <strong>{confirmAction.email}</strong>?
          </span>
          <div className="flex gap-2">
            <Button
              onClick={() => handleStatusChange(confirmAction.userId, confirmAction.action)}
              variant="primary"
              size="sm"
              busyLabel="Working…"
            >
              Confirm
            </Button>
            <Button
              onClick={() => setConfirmAction(null)}
              variant="secondary"
              size="sm"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {!showModal && (
      <>
      <div className="mb-2 flex flex-col sm:flex-row justify-between items-center gap-2">
        {/* Search on the left */}
        <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search users..."
          className="p-2 w-full sm:max-w-xs"
        />
        {/* Add button and pagination side by side on the right */}
        <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
          {canManageUsers && (
            <>
              <Button
                onClick={() => {
                  const cols = [
                    { field: 'id', header: 'ID' },
                    { field: 'email', header: 'Email' },
                    { field: 'role_name', header: 'Role' },
                    { field: 'is_active', header: 'Active' },
                  ];
                  exportToCsv('users.csv', users, cols);
                }}
                variant="secondary"
                size="md"
              >
                Export CSV
              </Button>
              <Button 
                onClick={openAddModal} 
                variant="primary"
                size="md"
              >
                Add User
              </Button>
            </>
          )}
          <Pager page={currentPage} pages={totalPages} onPage={setCurrentPage} />
        </div>
      </div>

      {/* Shared table — renders as cards at every breakpoint */}
      <div className="bg-white dark:bg-slate-900">
        <ResponsiveTable
          data={paginatedUsers}
          sort={userSort.sort}
          onSortChange={userSort.onSortChange}
          columns={columns}
          keyField="id"
          isLoading={false}
          emptyMessage="No users found."
          headerClassName="bg-gray-50 dark:bg-slate-800"
        />
      </div>
      </>
      )}

      {showModal && (
        <FullScreenPanel
          isOpen
          onClose={() => setShowModal(false)}
          title={selectedUser ? 'Edit User' : 'Add New User'}
        >
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                selectedUser ? updateUser() : addUser();
              }}
            >
              <div>
                <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Email</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`w-full ${formData.email.trim() ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'}`}
                  placeholder="email@example.com"
                />
                {formErrors.email && (
                  <p className="text-rose-500 text-xs mt-1">{formErrors.email}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Role</label>
                <select
                  value={formData.roleId}
                  onChange={(e) => setFormData({ ...formData, roleId: e.target.value })}
                  className={`w-full ${formData.roleId ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'}`}
                >
                  <option value="">Select a role</option>
                  {(selectedUser
                    ? roles // editing: show all active roles
                    : roles.filter(r => (r.name || '').toLowerCase() !== 'client') // adding: hide Client
                  ).map(role => (
                    <option key={role.id} value={role.id}>
                      {role.name}
                    </option>
                  ))}
                </select>
                {formErrors.roleId && (
                  <p className="text-rose-500 text-xs mt-1">{formErrors.roleId}</p>
                )}
              </div>
              <div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600"
                  />
                  <span className="text-sm text-gray-700 dark:text-slate-300">Active</span>
                </label>
              </div>
              <div className="flex justify-end gap-3 pt-2 border-t border-indigo-50 dark:border-slate-700">
                <Button type="button" onClick={() => setShowModal(false)} variant="secondary">Cancel</Button>
                <Button
                  type="submit"
                  variant="primary"
                  onClick={(e) => {
                    e.preventDefault();
                    return selectedUser ? updateUser() : addUser();
                  }}
                  busyLabel="Saving…"
                >
                  {selectedUser ? 'Update' : 'Add'} User
                </Button>
              </div>
            </form>
        </FullScreenPanel>
      )}
    </div>
  );
};

export default ManageUsers;